import { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  cycleNumber,
  formatDay,
  formatShortDay,
  formatUsdWithCode,
  formatWeekRange,
  resetDay,
} from "../../../../components/usageBilling/usageFormat";

// The cycle's headline numbers. Not Dashboard's KpiCard: that one counts up
// the integer part of its value, which would turn $0.0174 into 0.
function Tile({ label, value, note }) {
  return (
    <div className="min-w-0 bg-white rounded-xl border border-gray-200 border-l-4 border-l-orange-500 p-4 shadow-sm">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
        {label}
      </p>
      <p className="break-words text-2xl font-bold text-gray-900">{value}</p>
      {note && <p className="text-xs text-gray-500 mt-1">{note}</p>}
    </div>
  );
}

export default function UsageTotals({
  overview,
  overQuotaUsers,
  weekly,
  weeklyError,
  onRetryWeekly,
  language,
  formatMoney = formatUsdWithCode,
}) {
  const zh = language === "zh";
  const { totals } = overview;
  const isCurrent = Boolean(overview.current_week);
  const gemini = totals.breakdown_usd.gemini_input + totals.breakdown_usd.gemini_output;
  const gpt = totals.breakdown_usd.gpt_input + totals.breakdown_usd.gpt_output;
  const pastCycle = zh ? "過去的週期" : "Past cycle";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Tile
          label={zh ? "本週期花費" : "Cycle spend"}
          value={formatMoney(totals.used_usd)}
          note={`Gemini ${formatMoney(gemini)} · GPT ${formatMoney(gpt)}`}
        />
        <Tile
          label={zh ? "本週花費" : "This week"}
          value={isCurrent ? formatMoney(totals.week_used_usd) : "—"}
          note={
            isCurrent
              ? zh
                ? `第 ${overview.current_week.week_no} 週（共 4 週）`
                : `Week ${overview.current_week.week_no} of 4`
              : pastCycle
          }
        />
        <Tile
          label={zh ? "有使用的人數" : "Users with usage"}
          value={totals.users}
        />
        <Tile
          label={zh ? "超過本週額度" : "Over weekly quota"}
          value={isCurrent ? overQuotaUsers : "—"}
          note={
            !isCurrent
              ? pastCycle
              : overview.enforced
                ? zh
                  ? "已被封鎖"
                  : "Blocked from chatting"
                : zh
                  ? "未封鎖（限制關閉中）"
                  : "Not blocked: enforcement is off"
          }
        />
        <Tile label={zh ? "提問次數" : "Questions"} value={totals.turns} />
      </div>

      <WeeklySpend
        weekly={weekly}
        error={weeklyError}
        onRetry={onRetryWeekly}
        formatMoney={formatMoney}
        language={language}
      />
    </div>
  );
}

const weekTitle = (week, language) =>
  language === "zh"
    ? `第 ${cycleNumber(week.cycle_start)} 週期 · 第 ${week.week_no} 週 · ${formatWeekRange(week.week_start, week.week_end, language)}`
    : `Cycle ${cycleNumber(week.cycle_start)} · Week ${week.week_no} · ${formatWeekRange(week.week_start, week.week_end, language)}`;

const providerSplit = (week, formatMoney) =>
  `Gemini ${formatMoney(week.breakdown_usd.gemini_input + week.breakdown_usd.gemini_output)} · GPT ${formatMoney(week.breakdown_usd.gpt_input + week.breakdown_usd.gpt_output)}`;

function WeekTooltip({ active, payload, language, formatMoney }) {
  if (!active || !payload?.length) return null;
  const week = payload[0].payload.week;
  const zh = language === "zh";
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-md text-xs">
      <p className="text-gray-500 mb-1">
        {weekTitle(week, language)}
        {week.in_progress && (zh ? "（進行中）" : " (in progress)")}
      </p>
      <p className="text-gray-900">
        <span className="text-sm font-semibold">{formatMoney(week.used_usd)}</span>{" "}
        {zh ? "花費" : "spent"}
      </p>
      <p className="text-gray-600">{providerSplit(week, formatMoney)}</p>
      <p className="text-gray-600">
        {zh
          ? `${week.users} 位使用者 · 提問 ${week.turns} 次`
          : `${week.users} ${week.users === 1 ? "user" : "users"} · ${week.turns} ${week.turns === 1 ? "question" : "questions"}`}
      </p>
    </div>
  );
}

// Spend across all users per week (GET /admin/weekly), up to the selected
// cycle's last week. One series, so no legend box: the title names it. The
// running week is drawn lighter. Orange-600 clears 3:1 on the white panel.
function WeeklySpend({ weekly, error, onRetry, language, formatMoney }) {
  const zh = language === "zh";
  const [showTable, setShowTable] = useState(false);

  if (error) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        <span>
          {zh ? "無法載入每週花費：" : "Couldn't load spend per week: "}
          {error}
        </span>
        <button
          type="button"
          onClick={onRetry}
          className="shrink-0 font-medium underline"
        >
          {zh ? "重試" : "Retry"}
        </button>
      </div>
    );
  }
  if (!weekly || weekly.weeks.length === 0) return null;

  const weeks = [...weekly.weeks].reverse(); // the API is newest first
  const rows = weeks.map((week) => ({
    key: week.week_start,
    label: week.in_progress
      ? zh
        ? "本週"
        : "This week"
      : formatShortDay(week.week_start, language),
    used: week.used_usd,
    week,
  }));

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 flex flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {zh ? "每週花費" : "Spend per week"}
          </h3>
          <p className="text-sm text-gray-500">
            {zh
              ? `所有使用者，${weeks.length} 週`
              : `All users, ${weeks.length} ${weeks.length === 1 ? "week" : "weeks"}`}
          </p>
        </div>
        {weeks.length > 1 && (
          <button
            type="button"
            onClick={() => setShowTable((v) => !v)}
            className="text-xs font-medium text-orange-600 hover:underline"
          >
            {showTable
              ? zh
                ? "顯示圖表"
                : "Show chart"
              : zh
                ? "顯示表格"
                : "Show table"}
          </button>
        )}
      </div>

      {weeks.length === 1 ? (
        <p className="py-6 text-center text-sm text-gray-500">
          {zh
            ? `每週花費將於${formatDay(resetDay(weeks[0].week_end), language)}起顯示在這裡。`
            : `Spend per week appears here from ${formatDay(resetDay(weeks[0].week_end), language)}.`}
        </p>
      ) : showTable ? (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-gray-700">
            <thead>
              <tr className="text-xs text-gray-500">
                <th className="pb-1 text-left font-medium">{zh ? "週" : "Week"}</th>
                <th className="pb-1 text-right font-medium">{zh ? "花費" : "Spend"}</th>
                <th className="pb-1 text-right font-medium">Gemini</th>
                <th className="pb-1 text-right font-medium">GPT</th>
                <th className="pb-1 text-right font-medium">{zh ? "使用者" : "Users"}</th>
                <th className="pb-1 text-right font-medium">{zh ? "提問" : "Questions"}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 tabular-nums">
              {weekly.weeks.map((week) => (
                <tr key={week.week_start}>
                  <td className="py-2 pr-4 whitespace-nowrap">
                    {weekTitle(week, language)}
                    {week.in_progress && (
                      <span className="ml-1 text-xs text-gray-500">
                        {zh ? "（進行中）" : "(in progress)"}
                      </span>
                    )}
                  </td>
                  <td className="py-2 text-right font-semibold">{formatMoney(week.used_usd)}</td>
                  <td className="py-2 text-right">
                    {formatMoney(week.breakdown_usd.gemini_input + week.breakdown_usd.gemini_output)}
                  </td>
                  <td className="py-2 text-right">
                    {formatMoney(week.breakdown_usd.gpt_input + week.breakdown_usd.gpt_output)}
                  </td>
                  <td className="py-2 text-right">{week.users}</td>
                  <td className="py-2 text-right">{week.turns}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke="#e5e7eb" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "#6b7280" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={[0, "auto"]}
              tickFormatter={formatMoney}
              tick={{ fontSize: 11, fill: "#6b7280" }}
              axisLine={false}
              tickLine={false}
              width={96}
            />
            <Tooltip
              cursor={{ fill: "#f3f4f6" }}
              content={<WeekTooltip language={language} formatMoney={formatMoney} />}
            />
            <Bar
              dataKey="used"
              fill="#ea580c"
              radius={[4, 4, 0, 0]}
              maxBarSize={24}
              shape={(props) => (
                <Rectangle
                  {...props}
                  fillOpacity={props.payload.week.in_progress ? 0.55 : 1}
                />
              )}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
